import { Form } from 'utils/hooks'
import { Flex } from 'components/shared/flex'
import { BreastTestResultV2Form } from 'api/result'
import { CheckboxField, DateField, MultipleSelectField, RangeField, SelectField } from 'components/shared/input'
import { ProductItem } from 'entities/product_item'
import { Symptom } from '../result_v2'

type BreastTestViewProps = {
    form: Form<BreastTestResultV2Form>
    formPrev: Form<BreastTestResultV2Form>
    formPrev2: Form<BreastTestResultV2Form>
    productItem: ProductItem
    symptoms: Symptom[]
}

export const BreastTestView = ({ form, formPrev, formPrev2, productItem, symptoms }: BreastTestViewProps) => {
    return (
        <>
            <div style={{ padding: 10 }}>
                <div>状態</div>
                <Flex style={{ fontSize: 14 }}>
                    <CheckboxField form={form} attr={'menstruation'} checkboxLabel="生理中" />
                    <CheckboxField form={form} attr={'pregnancy'} checkboxLabel="妊娠中" />
                </Flex>
            </div>
            <div style={{ padding: 10 }}>
                <div>疑いのある症状</div>

                <div style={{ fontSize: 14 }}>
                    <MultipleSelectField
                        id="breast-symptoms"
                        form={form}
                        attr="symptoms"
                        selectItems={symptoms.filter((s) => s.products.includes('breast')).map((s) => s)}
                        hideSearch
                        horizontal
                        hideSelectAll
                    />
                </div>
            </div>
            <div>
                <BreastControlForm form={form} title="今回" color="#9002" hideEnableCheck productItem={productItem} />
                <BreastControlForm form={formPrev} title="前回" color="#9002" productItem={productItem} />
                <BreastControlForm form={formPrev2} title="前々回" color="#9002" productItem={productItem} />
            </div>
        </>
    )
}
type BreastControlFormProps = {
    form: Form<BreastTestResultV2Form>
    title: string
    color: string
    hideEnableCheck?: boolean
    productItem: ProductItem
}

const BreastControlForm = ({ form, title, color, hideEnableCheck, productItem }: BreastControlFormProps) => {
    const enabled = form.object.enabled

    return (
        <div style={{ padding: 20, backgroundColor: color, width: '100%' }}>
            <Flex style={{}}>
                <div style={{ fontWeight: 'bold' }}>{title}</div>
                {!hideEnableCheck && (
                    <div>
                        <CheckboxField form={form} attr="enabled" checkboxLabel="有効" controlId={title} />
                    </div>
                )}
            </Flex>
            {form.object.enabled && (
                <>
                    <Flex>
                        <DateField form={form} attr="deliveryDate" label="検査日" style={{ marginBottom: 0 }} />
                    </Flex>
                    {form.object.useCt ? (
                        <div>
                            <div style={{ backgroundColor: '#fff5', padding: 5 }}>
                                <div style={{ fontWeight: 'bold' }}>HK</div>
                                <div style={{ padding: 10 }}>
                                    {productItem.qcVersion?.protocolVersionProbes
                                        ?.filter((pvp) => pvp.qcType === 'HK')
                                        .map((pvp, index) => {
                                            return (
                                                <RangeField
                                                    key={'range-' + index}
                                                    form={form}
                                                    attr={['hkCts', pvp.probe!.name!]}
                                                    min={0}
                                                    max={40}
                                                    label={`${pvp.probe?.name} CT =`}
                                                    showValue
                                                    step={0.01}
                                                />
                                            )
                                        })}
                                </div>
                            </div>
                            <div style={{ backgroundColor: '#fff5', marginTop: 10, padding: 5 }}>
                                <div style={{ fontWeight: 'bold' }}>Bio marker</div>
                                <div style={{ padding: 10 }}>
                                    {productItem.currentProtocolVersion?.protocolVersionProbes?.map((pvp, index) => {
                                        return (
                                            <RangeField
                                                key={'range-' + index}
                                                form={form}
                                                attr={['markerCts', pvp.probe!.name!]}
                                                min={0}
                                                max={40}
                                                label={`${pvp.probe?.name} CT =`}
                                                showValue
                                                step={0.01}
                                            />
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <RangeField
                            form={form}
                            attr="baseScore"
                            label="機械学習スコア"
                            min={0}
                            max={1}
                            step={0.001}
                            disabled={!enabled}
                            style={{ marginBottom: 0 }}
                            showValue
                        />
                    )}
                    <RangeField form={form} attr="age" label="年齢" disabled={!enabled} min={0} max={100} step={5} style={{ marginBottom: 0 }} showValue />
                    <Flex>
                        <RangeField
                            form={form}
                            attr="height"
                            label="身長"
                            disabled={!enabled}
                            min={100}
                            max={200}
                            step={5}
                            style={{ marginBottom: 0 }}
                            showValue
                        />
                        <RangeField
                            form={form}
                            attr="weight"
                            label="体重"
                            disabled={!enabled}
                            min={30}
                            max={120}
                            step={5}
                            style={{ marginBottom: 0 }}
                            showValue
                        />
                    </Flex>
                    <Flex>
                        <SelectField
                            form={form}
                            attr="smoking"
                            label="喫煙歴"
                            selectItems={[
                                { label: 'なし', value: 'never' },
                                { label: '過去に吸っていた', value: 'ever' },
                                { label: '吸っている', value: 'now' },
                            ]}
                        />
                        <SelectField
                            form={form}
                            attr="alcoholPerWeek"
                            label="飲酒歴"
                            selectItems={[
                                { label: 'なし', value: 0 },
                                { label: '週1日未満', value: 0.5 },
                                { label: '週1日', value: 1 },
                                { label: '週2日', value: 2 },
                                { label: '週3日', value: 3 },
                                { label: '週4日', value: 4 },
                                { label: '週5日', value: 5 },
                                { label: '週6日', value: 6 },
                                { label: '毎日', value: 7 },
                            ]}
                        />
                    </Flex>
                    <Flex>
                        <RangeField
                            form={form}
                            attr="birthCount"
                            label="出産回数"
                            disabled={!enabled}
                            min={0}
                            max={10}
                            step={1}
                            style={{ marginBottom: 0 }}
                            defaultValue={0}
                            showValue
                        />
                        <RangeField
                            form={form}
                            attr="ageAtFirstBirth"
                            label="初産年齢"
                            disabled={!enabled}
                            min={0}
                            max={60}
                            step={10}
                            style={{ marginBottom: 0 }}
                            defaultValue={0}
                            showValue
                        />
                    </Flex>
                    <div style={{ marginTop: 10 }}>
                        <CheckboxField form={form} attr="mammography" checkboxLabel="マンモグラフィを受けたことがある" />
                        {form.object.mammography && <CheckboxField form={form} attr="mammographyResult" checkboxLabel="マンモグラフィ異常あり" />}
                    </div>
                    <SelectField
                        form={form}
                        attr="exercise"
                        label="運動習慣"
                        selectItems={[
                            { label: '~週1回程度', value: 'unusual' },
                            { label: '週に2〜3回程度', value: 'sometimes' },
                            { label: '週4回以上', value: 'everyday' },
                        ]}
                    />
                    <div style={{ marginTop: 10 }}>
                        <div>
                            <div>既往歴</div>
                            <div>
                                <CheckboxField
                                    form={form}
                                    attr="diabetes"
                                    checkboxLabel="糖尿病"
                                    disabled={!enabled}
                                    controlId={title}
                                    style={{ marginBottom: 0 }}
                                />
                            </div>
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <div>家族歴</div>
                            <div>
                                <RangeField
                                    showValue
                                    form={form}
                                    attr="firstDegreeRelative"
                                    label="母・姉妹・娘（人）"
                                    disabled={!enabled}
                                    step={1}
                                    min={0}
                                    max={5}
                                />
                                <RangeField
                                    showValue
                                    form={form}
                                    attr="secondDegreeOrHigher"
                                    label="それ以外（祖母・叔母・いとこなど）（人）"
                                    disabled={!enabled}
                                    step={1}
                                    min={0}
                                    max={5}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
